import { useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { Badge } from '@marine/ui/badge';
import { Button } from '@marine/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@marine/ui/collapsible';
import { cn } from '@/lib/utils';
import type { NotImportedOptionLine } from '@/modules/inventory/types/boat.types';

/** Why the merge skipped a line, as the dealer reads it (#1182 §1.6). */
const REASON_LABELS: Record<NotImportedOptionLine['reason'], string> = {
  configuration: 'Configuration',
  negation: 'Not included',
  fee: 'Fee',
  boat_unit: 'The boat itself',
  engine: 'Engine',
  trailer: 'Trailer',
  credit: 'Credit',
};

export function notImportedLineKey(line: NotImportedOptionLine): string {
  return `${line.source_document_id}:${line.type}:${line.item}:${line.sku ?? ''}`;
}

function amountOf(line: NotImportedOptionLine): string | null {
  const amount = line.cost ?? line.price;
  if (amount === null) return null;
  return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

interface NotImportedOptionsListProps {
  lines: NotImportedOptionLine[];
  onPullBack: (line: NotImportedOptionLine) => void;
  /** Lines already pulled back, hidden until the server stops listing them. */
  isPulledBack?: (line: NotImportedOptionLine) => boolean;
  /** The line being saved (its Add button is disabled). */
  pendingKey?: string | null;
}

/**
 * #1182 (spec §1.6) — option lines from the documents that were not imported as
 * equipment (the boat unit, its engine or trailer, fees, negations, configuration
 * choices, credits). Collapsed by default; "Add" pulls a line back as a dealer row.
 */
export function NotImportedOptionsList({ lines, onPullBack, isPulledBack, pendingKey = null }: NotImportedOptionsListProps) {
  const [open, setOpen] = useState(false);
  const visible = lines.filter((line) => !isPulledBack?.(line));
  if (visible.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-md border border-border" data-testid="not-imported-options">
      <CollapsibleTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="w-full justify-between">
          <span>{`Not imported (${visible.length})`}</span>
          <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} aria-hidden="true" />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <p className="px-3 pt-1 text-[0.8125rem] text-muted-foreground">
          Lines from the documents that were not added as equipment. Add one if it belongs.
        </p>
        <ul className="divide-y divide-border">
          {visible.map((line) => {
            const key = notImportedLineKey(line);
            const amount = amountOf(line);
            return (
              <li key={key} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="block truncate">{line.item}</span>
                  <span className="flex items-center gap-2 text-[0.8125rem] text-muted-foreground">
                    <Badge variant="secondary" size="sm">{REASON_LABELS[line.reason]}</Badge>
                    {amount && <span>{amount}</span>}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pendingKey === key}
                  onClick={() => onPullBack(line)}
                  aria-label={`Add ${line.item} to ${line.type} equipment`}
                >
                  <Plus className="size-3.5 me-1" aria-hidden="true" />
                  Add
                </Button>
              </li>
            );
          })}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}
